import {
  parseInTimezone,
  parseISOWithOffset,
  parseToDateTime,
  toTimezone,
  addMinutes,
  formatTime as datetimeFormatTime,
  formatDate as datetimeFormatDate,
  strftimeToLuxonFormat
} from '@calendly/datetime'

import { i18n } from 'locales'

const ISO_OFFSET_PATTERN = /(Z|[+-]\d{2}:?\d{2})$/
const UTC_INSTANT_PATTERN = /(?:Z|\+00:?00)$/i
const VANCOUVER_TIMEZONE = 'America/Vancouver'
const VANCOUVER_FIXED_OFFSET = 'UTC-7'
const VANCOUVER_PERMANENT_OFFSET_START = Date.parse('2026-11-01T09:00:00Z')
const WINNIPEG_TIMEZONE = 'America/Winnipeg'
const WINNIPEG_FIXED_OFFSET = 'UTC-5'
// Former America/Winnipeg rules fell back at 02:00 CDT. That instant is
// 2026-11-01T07:00:00Z. tzdata 2026e keeps Winnipeg on UTC−5 instead.
const WINNIPEG_PERMANENT_OFFSET_START = Date.parse('2026-11-01T07:00:00Z')
const TRUST_ISO_OFFSET_TIMEZONES = new Set([
  'Africa/Casablanca',
  'Africa/El_Aaiun',
  'America/Winnipeg'
])

const honorsBackendOffset = (time, timezone) => {
  if (typeof time !== 'string' || !ISO_OFFSET_PATTERN.test(time)) return false
  if (!TRUST_ISO_OFFSET_TIMEZONES.has(timezone)) return false
  // Booked events arrive as UTC instants. A Z or +00:00 suffix is not a
  // Winnipeg wall-clock offset; spot lists use an explicit offset such as −05:00.
  if (timezone === WINNIPEG_TIMEZONE && UTC_INSTANT_PATTERN.test(time)) return false
  return true
}

export const parseLocalizedSpotTime = (time, timezone) => {
  if (honorsBackendOffset(time, timezone)) {
    // Honor the backend offset when older browser tzdata still applies the former
    // rule. Morocco is permanent UTC as of 2026-09-20 (tzdata 2026c). Winnipeg is
    // permanent UTC−5 as of 2026-11-01, so 09:00-05:00 must not become 08:00.
    return parseISOWithOffset(time)
  }

  if (timezone === WINNIPEG_TIMEZONE && typeof time === 'string' && UTC_INSTANT_PATTERN.test(time)) {
    const instant = parseToDateTime(time)
    if (instant && instant.toMillis() >= WINNIPEG_PERMANENT_OFFSET_START) {
      // Older browser tzdata still falls Winnipeg back to UTC−6 after this
      // instant, so a UTC booked time has to be shifted by the permanent offset.
      return toTimezone(instant, WINNIPEG_FIXED_OFFSET)
    }
  }

  if (timezone !== VANCOUVER_TIMEZONE || typeof time !== 'string') {
    return parseInTimezone(time, timezone)
  }

  if (!ISO_OFFSET_PATTERN.test(time)) return parseInTimezone(time, timezone)

  const instant = parseToDateTime(time)
  if (!instant || instant.toMillis() < VANCOUVER_PERMANENT_OFFSET_START) {
    return parseInTimezone(time, timezone)
  }

  // Vancouver adopted permanent UTC−7 in 2026, but some supported browsers
  // still apply the former UTC−8 winter rule. This compatibility path can be
  // removed once supported runtimes include IANA tzdata 2026b or newer.
  return toTimezone(instant, VANCOUVER_FIXED_OFFSET)
}

export const formatDate = (time, format, locale) => {
  const luxonFormat = strftimeToLuxonFormat(format || i18n.t('time.formats.date_full'))
  const currentLocale = locale || i18n.locale
  return datetimeFormatDate(time, { format: luxonFormat, locale: currentLocale })
}

export const buildSpot = (startTime, duration, timezone) => {
  const start = parseLocalizedSpotTime(startTime, timezone)
  return {
    start_time: start,
    end_time: addMinutes(start, duration)
  }
}

export const formatEventTime = (spot, notation, timezone) => {
  const startTime = timezone ? parseLocalizedSpotTime(spot.start_time, timezone) : spot.start_time
  const endTime = timezone ? parseLocalizedSpotTime(spot.end_time, timezone) : spot.end_time
  const startDate = formatDate(startTime)
  const startFormatted = datetimeFormatTime(startTime, { notation })
  const endFormatted = datetimeFormatTime(endTime, { notation })
  return `${startFormatted} - ${endFormatted},\n ${startDate}`
}

export const formatEventTimeToDisplay = (spot, notation, timezone) => {
  const startTime = timezone ? parseLocalizedSpotTime(spot.start_time, timezone) : spot.start_time
  const endTime = timezone ? parseLocalizedSpotTime(spot.end_time, timezone) : spot.end_time
  return `${datetimeFormatTime(startTime, { notation })} - ${datetimeFormatTime(endTime, { notation })}`
}

export const formatEnterDetailsSpotHeadingLine = (spot, notation, timezone, locale) => {
  const startTime = timezone ? parseLocalizedSpotTime(spot.start_time, timezone) : spot.start_time
  const endTime = timezone ? parseLocalizedSpotTime(spot.end_time, timezone) : spot.end_time
  const dateFormat = i18n.t('time.formats.enter_details_spot_date')
  const datePart = formatDate(startTime, dateFormat, locale)
  const startFormatted = datetimeFormatTime(startTime, { notation })
  const endFormatted = datetimeFormatTime(endTime, { notation })
  return `${datePart}, ${startFormatted} – ${endFormatted}`
}

const formatShortTimezoneAbbrev = (dt, ianaTz, locale) => {
  if (!dt?.isValid || !ianaTz) return ''
  const loc = String(locale || 'en').replace('_', '-')
  try {
    const parts = new Intl.DateTimeFormat(loc, {
      timeZone: ianaTz,
      timeZoneName: 'short'
    }).formatToParts(dt.toJSDate())
    return parts.find((p) => p.type === 'timeZoneName')?.value?.trim() || ''
  } catch {
    return typeof dt.offsetNameShort === 'string' ? dt.offsetNameShort : ''
  }
}

/** Matches reschedule Enter Details screenshot: "Weekday, Mon D, YYYY, h:mm am — h:mm am (TZ)" */
export const formatRescheduleEnterDetailsRangeLine = (source, notation, timezoneId, locale) => {
  if (!source?.start_time || !source?.end_time || !timezoneId) return ''
  const startTime = parseLocalizedSpotTime(source.start_time, timezoneId)
  const endTime = parseLocalizedSpotTime(source.end_time, timezoneId)
  if (!startTime?.isValid || !endTime?.isValid) return ''
  const dateFormat = i18n.t('time.formats.enter_details_spot_date')
  const datePart = formatDate(startTime, dateFormat, locale)
  const startFormatted = datetimeFormatTime(startTime, { notation })
  const endFormatted = datetimeFormatTime(endTime, { notation })
  const tzAbbrev = formatShortTimezoneAbbrev(startTime, timezoneId, locale)
  const emDash = '\u2014'
  const tzPart = tzAbbrev ? ` (${tzAbbrev})` : ''
  return `${datePart}, ${startFormatted} ${emDash} ${endFormatted}${tzPart}`
}
